import type { WatchRoom } from './watchSocket.constants';
import type { WatchAssetPayload, WatchInboundMessage, WatchPayload } from './watchSocket.types';
import { parseInboundMessage } from './watchSocketMessages';

/** One InfluxDB point as the cloud proxy relays it: ``{ measurement, tags, fields, timestamp }``. */
interface DataPoint {
  tags?: Record<string, unknown>;
  fields: WatchPayload;
  timestamp?: number;
}

const MAIN_INPUT_COMPONENT_ID = 'main_input';

const COMPONENT_ROOMS = [
  'components/input_data',
  'components/container_data',
  'components/rtu_data',
  'components/plc_data',
  'components/bms_data',
] as const;

type ComponentRoom = (typeof COMPONENT_ROOMS)[number];

const isPlainObject = (value: unknown): value is WatchPayload =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isDataPoint = (value: unknown): value is DataPoint => isPlainObject(value) && isPlainObject(value.fields);

const isComponentRoom = (room: WatchRoom): room is ComponentRoom =>
  (COMPONENT_ROOMS as readonly string[]).includes(room);

const withTimestamp = ({ fields, timestamp }: DataPoint): WatchPayload => ({
  ...(typeof timestamp === 'number' ? { ts_measured: timestamp } : {}),
  ...fields,
});

const last = <T>(items: T[]): T => items[items.length - 1];

/**
 * Cloud counterpart of ``parseInboundMessage``: every data room arrives as a list of points
 * (``{ payload: [{ measurement, tags, fields, timestamp }] }``) instead of the per-room object the local
 * proxy relays. Each room is rewritten into the local shape its consumers already read. Anything that
 * is not a point list - feedback, config announcements, metadata - is left to the shared parser.
 */
export const parseCloudInboundMessage = (room: string, rawEnvelope: unknown): WatchInboundMessage | null => {
  const local = parseInboundMessage(room, rawEnvelope);

  if (!isPlainObject(rawEnvelope) || !('payload' in rawEnvelope)) {
    return local;
  }

  const payload = rawEnvelope.payload;
  const points = (Array.isArray(payload) ? payload : [payload]).filter(isDataPoint);

  if (points.length === 0) {
    return local;
  }

  switch (room) {
    case 'state':
      return { room, payload: { state: withTimestamp(last(points)) } };

    case 'timeseries':
      return { room, payload: { ts: withTimestamp(last(points)) } };

    case 'assets/state':
    case 'assets/timeseries': {
      // One point per asset: the consumers key them by asset id, so none may be dropped.
      const assets = points.map(
        (point): WatchAssetPayload => ({
          tags: (point.tags ?? {}) as WatchAssetPayload['tags'],
          fields: withTimestamp(point),
        })
      );

      return {
        room,
        payload: assets[assets.length - 1],
        assets: Object.fromEntries(
          assets
            .filter((item) => item.tags.asset_id !== undefined)
            .map((item) => [`${item.tags.asset_type}:${item.tags.asset_id}`, item])
        ),
      };
    }

    default: {
      if (!isComponentRoom(room as WatchRoom)) {
        return local;
      }

      // input_data also carries the second inverter, which must not replace the main input's data.
      const candidates =
        room === 'components/input_data'
          ? points.filter(
              ({ tags }) => tags?.component_id === undefined || tags.component_id === MAIN_INPUT_COMPONENT_ID
            )
          : points;

      if (candidates.length === 0) {
        return null;
      }

      return {
        room,
        payload: { stream_identifier: room.split('/')[1], fields: last(candidates).fields },
      } as WatchInboundMessage;
    }
  }
};
